import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type { ApiConfiguration, NewApiConfiguration } from '../types';
import { getUserData } from '../utils';

// Local cache for settings to avoid repeated queries
const settingsCache = {
  data: null as { whatsapp_global_config?: boolean } | null,
  timestamp: 0,
  // Cache expiration time: 5 minutes
  expiration: 5 * 60 * 1000,
};

// Dedup delle richieste in volo: al load due chiamanti concorrenti bucavano
// entrambi la cache e facevano due volte la stessa query (~1s l'una)
let whatsappFlagPromise: Promise<{ whatsapp_global_config?: boolean } | null> | null = null;

async function getWhatsAppGlobalConfigFlag(organizationId: string): Promise<boolean> {
  const now = Date.now();
  if (settingsCache.data && now - settingsCache.timestamp < settingsCache.expiration) {
    return settingsCache.data.whatsapp_global_config !== false;
  }
  if (!whatsappFlagPromise) {
    whatsappFlagPromise = supabase
      .from('settings')
      .select('whatsapp_global_config')
      .eq('organization_id', organizationId)
      .single()
      .then(({ data, error }) => {
        if (!error && data && 'whatsapp_global_config' in data) {
          settingsCache.data = data;
          settingsCache.timestamp = Date.now();
        }
        return data ?? null;
      })
      .finally(() => { whatsappFlagPromise = null; });
  }
  const data = await whatsappFlagPromise.catch(() => null);
  // Default: global config
  return data ? data.whatsapp_global_config !== false : true;
}

// La colonna user_id esiste da tempo: il check schema va fatto una volta sola
let hasUserIdColumnCache: boolean | null = null;

async function checkHasUserIdColumn(): Promise<boolean> {
  if (hasUserIdColumnCache !== null) return hasUserIdColumnCache;
  const { error: columnError } = await supabase
    .from('api_configurations')
    .select('user_id')
    .limit(1);
  hasUserIdColumnCache = !(columnError && columnError.code === '42703');
  return hasUserIdColumnCache;
}

// Tipo per lo stato di salute WhatsApp
type WahaHealthStatus = 'healthy' | 'warning' | 'error' | 'unknown' | null;

// Issue per singola sessione WAHA. Mostrato nel tooltip del badge sidebar
// quando l'utente fa hover sul triangolino di alert.
export interface WahaSessionIssue {
  configId: string;
  label: string;             // es. "Flusia · +393932787927" o session name
  kind: 'session_broken' | 'high_failure_rate';
  detail: string;             // testo umano per tooltip
  failedLastHour?: number;
  sentLastHour?: number;
}

interface ApiConfigurationStore {
  configurations: ApiConfiguration[];
  loading: boolean;
  error: string | null;
  // WhatsApp health monitoring (multi-session aggregato)
  wahaHealthStatus: WahaHealthStatus;
  wahaHealthMessage: string | null;
  wahaLastCheck: string | null;
  wahaSessionsIssues: WahaSessionIssue[]; // lista numeri con problemi (vuota = tutto OK)
  fetchConfigurations: (forceRefresh?: boolean) => Promise<void>;
  createConfiguration: (config: NewApiConfiguration) => Promise<ApiConfiguration | null>;
  updateConfiguration: (id: string, config: Partial<ApiConfiguration>) => Promise<ApiConfiguration | null>;
  deleteConfiguration: (id: string) => Promise<boolean>;
  // WhatsApp health functions
  fetchWahaHealth: () => Promise<void>;
  subscribeToWahaHealth: () => () => void;
}

export const useApiConfigurationStore = create<ApiConfigurationStore>((set, get) => ({
  configurations: [],
  loading: false,
  error: null,
  // WhatsApp health state
  wahaHealthStatus: null,
  wahaHealthMessage: null,
  wahaLastCheck: null,
  wahaSessionsIssues: [],

  fetchConfigurations: async (forceRefresh = false) => {
    // If we already have configurations and it's not a forced refresh, skip
    if (get().configurations.length > 0 && !forceRefresh) {
      console.log('[API] Using cached API configurations');
      return;
    }
    
    set({ loading: true, error: null });
    try {
      // Get user data from the app-wide context if possible
      let organizationId, userId;
      
      // Use the centralized getUserData utility instead of direct context access
      try {
        const userData = await getUserData();
        organizationId = userData.organizationId;
        userId = userData.userId;
        console.log('[API] Using cached user data for API configurations');
      } catch (e) {
        // Fallback to direct query if getting user data fails
        console.error('Error getting cached user data, querying directly:', e);
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) throw new Error('User not authenticated');
        
        userId = userData.user.id;
        
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', userId)
          .single();
        
        if (!userProfile?.organization_id) throw new Error('User not associated with an organization');
        organizationId = userProfile.organization_id;
      }
      
      if (!organizationId) throw new Error('No organization ID found');

      // Config mode WhatsApp: cache condivisa + dedup richieste in volo
      const isGlobalWhatsAppConfig = await getWhatsAppGlobalConfigFlag(organizationId);

      // Build query based on configuration mode
      let query = supabase
        .from('api_configurations')
        .select('*')
        .eq('organization_id', organizationId);

      // First check if user_id column exists in the table (risultato cachato)
      try {
        const hasUserIdColumn = await checkHasUserIdColumn();

        if (hasUserIdColumn) {
          // Column exists, use the normal query
          if (isGlobalWhatsAppConfig) {
            // For global mode: fetch all configs where either:
            // 1. service_name is not 'waha', or
            // 2. service_name is 'waha' and user_id is null (organization-wide), or
            // 3. service_name is 'waha' and user_id matches current user (fallback)
            query = query.or(`service_name.neq.waha,and(service_name.eq.waha,user_id.is.null),and(service_name.eq.waha,user_id.eq.${userId})`);
          } else {
            // For per-user mode: fetch all configs where either:
            // 1. service_name is not 'waha', or
            // 2. service_name is 'waha' and user_id matches the current user
            query = query.or(`service_name.neq.waha,and(service_name.eq.waha,user_id.eq.${userId})`);
          }
        } else {
          // Column doesn't exist yet, just query by service_name and skip user filtering
          console.warn('user_id column not found in api_configurations, skipping user filtering');
        }
      } catch (error) {
        console.warn('Error checking for user_id column:', error);
        // Just continue with the basic query without filters
      }

      // Execute the query with ordering
      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      set({ configurations: data || [] });
    } catch (error) {
      set({ error: friendlyDbError(error) });
      console.error('Error fetching API configurations:', error);
    } finally {
      set({ loading: false });
    }
  },

  createConfiguration: async (config) => {
    try {
      // Get user data from the app-wide context if possible
      let organizationId, userId;
      
      try {
        // This will throw an error if used outside the UserContext provider
        const userData = useUserData();
        organizationId = userData.organizationId;
        userId = userData.userId;
        
        console.log('Using UserContext data for creating API configuration');
      } catch (e) {
        // Fallback to direct query if context is not available
        console.log('UserContext not available, querying user data directly');
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) throw new Error('User not authenticated');
        
        userId = userData.user.id;
        
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', userId)
          .single();
        
        if (!userProfile?.organization_id) throw new Error('User not associated with an organization');
        organizationId = userProfile.organization_id;
      }
      
      if (!organizationId) throw new Error('No organization ID found');

      // Check if this is a WhatsApp config - if so, we need to check the config mode
      const configWithOrgAndUser = {
        ...config,
        organization_id: organizationId
      };

      if (config.service_name === 'waha') {
        // Config mode WhatsApp: cache condivisa + dedup richieste in volo
        const isGlobalWhatsAppConfig = await getWhatsAppGlobalConfigFlag(organizationId);

        // For per-user mode, add the user_id to the configuration
        if (!isGlobalWhatsAppConfig) {
          try {
            if (await checkHasUserIdColumn()) {
              // Column exists, add the user_id
              configWithOrgAndUser.user_id = userId;
            } else {
              console.warn('user_id column not found in api_configurations, cannot set per-user configuration');
              // Continue without user_id - it will be set when the migration runs
            }
          } catch (error) {
            console.warn('Error checking for user_id column:', error);
            // Continue without checking user_id column
          }
        }
      }

      const { data, error } = await supabase
        .from('api_configurations')
        .insert(configWithOrgAndUser)
        .select()
        .single();

      if (error) throw error;

      const configurations = get().configurations;
      set({ configurations: [data, ...configurations] });

      // Marca task SetupChecklist: WAHA = whatsapp, altri = integrations
      try {
        const taskId = config.service_name === 'waha' ? 'whatsapp' : 'integrations';
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('onboarding:task-completed', { detail: { taskId } }),
          );
        }
      } catch {
        // ignore
      }

      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      console.error('Error creating API configuration:', error);
      return null;
    }
  },

  updateConfiguration: async (id, config) => {
    try {
      // Get user data from the app-wide context if possible
      let organizationId;
      
      try {
        const userData = await getUserData();
        organizationId = userData.organizationId;
        console.log('[API] Using cached user data for updating API configuration');
      } catch (e) {
        // Fallback to direct query if getting user data fails
        console.error('Error getting cached user data, querying directly:', e);
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) throw new Error('User not authenticated');
        
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', userData.user.id)
          .single();
        
        if (!userProfile?.organization_id) throw new Error('User not associated with an organization');
        organizationId = userProfile.organization_id;
      }
      
      if (!organizationId) throw new Error('No organization ID found');

      const { data, error } = await supabase
        .from('api_configurations')
        .update(config)
        .eq('id', id)
        .eq('organization_id', organizationId) // Ensure we only update configurations for this organization
        .select()
        .single();

      if (error) throw error;

      const configurations = get().configurations.map(c => 
        c.id === id ? { ...c, ...data } : c
      );
      set({ configurations });
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteConfiguration: async (id) => {
    try {
      // Get user data from the app-wide context if possible
      let organizationId;
      
      try {
        const userData = await getUserData();
        organizationId = userData.organizationId;
        console.log('[API] Using cached user data for deleting API configuration');
      } catch (e) {
        // Fallback to direct query if getting user data fails
        console.error('Error getting cached user data, querying directly:', e);
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) throw new Error('User not authenticated');
        
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', userData.user.id)
          .single();
        
        if (!userProfile?.organization_id) throw new Error('User not associated with an organization');
        organizationId = userProfile.organization_id;
      }
      
      if (!organizationId) throw new Error('No organization ID found');

      const { error } = await supabase
        .from('api_configurations')
        .delete()
        .eq('id', id)
        .eq('organization_id', organizationId); // Ensure we only delete configurations for this organization

      if (error) throw error;

      const configurations = get().configurations.filter(c => c.id !== id);
      set({ configurations });
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  // Fetch WhatsApp session health status
  fetchWahaHealth: async () => {
    try {
      // Get organization ID and user ID
      let organizationId: string | undefined;
      let userId: string | undefined;

      try {
        const userData = await getUserData();
        organizationId = userData.organizationId;
        userId = userData.userId;
      } catch (e) {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return;
        userId = userData.user.id;
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', userData.user.id)
          .single();
        if (!userProfile?.organization_id) return;
        organizationId = userProfile.organization_id;
      }

      if (!organizationId || !userId) return;

      // (Espanso 2026-05-30) Carica TUTTE le sessioni accessibili all'utente:
      // 1. config user-specific (proprie)
      // 2. config globali dell'org (visibili a tutti)
      // 3. config globali con accesso esplicito via whatsapp_user_access
      // Per OGNI sessione, controlla session_health_status raw + failure
      // rate ultima ora dalla queue (segnalazione applicativa proattiva).
      const [{ data: ownConfigs }, { data: globalConfigs }, { data: accessRows }] = await Promise.all([
        supabase
          .from('api_configurations')
          .select('id, display_name, configuration, session_health_status, session_health_message, session_last_check')
          .eq('service_name', 'waha')
          .eq('organization_id', organizationId)
          .eq('user_id', userId)
          .eq('is_active', true),
        supabase
          .from('api_configurations')
          .select('id, display_name, configuration, session_health_status, session_health_message, session_last_check')
          .eq('service_name', 'waha')
          .eq('organization_id', organizationId)
          .is('user_id', null)
          .eq('is_active', true),
        supabase
          .from('whatsapp_user_access')
          .select('configuration_id')
          .eq('user_id', userId),
      ]);

      // Filtra global config in base a access list (se esistono row di restriction
      // per quella config, l'utente DEVE essere nella lista; altrimenti tutti la vedono).
      const accessByConfig = new Map<string, Set<string>>();
      const { data: allAccessRows } = await supabase
        .from('whatsapp_user_access')
        .select('configuration_id, user_id')
        .eq('organization_id', organizationId);
      for (const row of allAccessRows ?? []) {
        if (!accessByConfig.has(row.configuration_id)) accessByConfig.set(row.configuration_id, new Set());
        accessByConfig.get(row.configuration_id)!.add(row.user_id);
      }
      const myAccessSet = new Set((accessRows ?? []).map((a: any) => a.configuration_id));
      const visibleGlobals = (globalConfigs ?? []).filter((cfg: any) => {
        const restricted = accessByConfig.get(cfg.id);
        if (!restricted || restricted.size === 0) return true; // no restriction → visibile a tutti
        return myAccessSet.has(cfg.id);
      });

      const accessibleConfigs = [
        ...(ownConfigs ?? []),
        ...visibleGlobals,
      ];

      if (accessibleConfigs.length === 0) {
        set({
          wahaHealthStatus: null,
          wahaHealthMessage: null,
          wahaLastCheck: null,
          wahaSessionsIssues: [],
        });
        return;
      }

      // Failure rate ultima ora per ogni config accessibile
      const oneHourAgo = new Date(Date.now() - 3600_000).toISOString();
      const configIds = accessibleConfigs.map((c: any) => c.id);
      const { data: queueRows } = await supabase
        .from('whatsapp_queue')
        .select('whatsapp_config_id, status, error_message')
        .in('whatsapp_config_id', configIds)
        .gte('updated_at', oneHourAgo)
        .in('status', ['sent', 'failed']);

      const statsByConfig = new Map<string, { sent: number; failed: number; topError: string | null }>();
      for (const r of queueRows ?? []) {
        const k = r.whatsapp_config_id as string;
        if (!k) continue;
        if (!statsByConfig.has(k)) statsByConfig.set(k, { sent: 0, failed: 0, topError: null });
        const s = statsByConfig.get(k)!;
        if (r.status === 'sent') s.sent++;
        else {
          s.failed++;
          if (!s.topError && r.error_message) s.topError = r.error_message as string;
        }
      }

      // Costruisci lista issues
      const FAILURE_RATE_THRESHOLD = 0.15;
      const MIN_SAMPLE = 5;
      const issues: WahaSessionIssue[] = [];
      for (const cfg of accessibleConfigs as any[]) {
        const phone = cfg.configuration?.phone_number?.replace(/@.*$/, '');
        const label = cfg.display_name
          ? `${cfg.display_name}${phone ? ` · +${phone}` : ''}`
          : (phone ? `+${phone}` : 'Numero senza etichetta');

        // 1. Session broken (raw health status)
        const rawStatus = (cfg.session_health_status ?? '').toString().toUpperCase();
        const sessionBroken = rawStatus &&
          rawStatus !== 'WORKING' &&
          rawStatus !== 'HEALTHY' &&
          rawStatus !== 'OK';
        if (sessionBroken) {
          issues.push({
            configId: cfg.id,
            label,
            kind: 'session_broken',
            detail: cfg.session_health_message
              ? `Sessione: ${cfg.session_health_message}`
              : `Sessione in stato ${rawStatus} — riscansiona il QR`,
          });
          continue;
        }

        // 2. High failure rate (app-level)
        const stats = statsByConfig.get(cfg.id);
        if (stats) {
          const total = stats.sent + stats.failed;
          if (total >= MIN_SAMPLE) {
            const rate = stats.failed / total;
            if (rate >= FAILURE_RATE_THRESHOLD) {
              issues.push({
                configId: cfg.id,
                label,
                kind: 'high_failure_rate',
                detail: `${stats.failed}/${total} falliti ultima ora${stats.topError ? ` — ${stats.topError.substring(0, 60)}` : ''}`,
                failedLastHour: stats.failed,
                sentLastHour: stats.sent,
              });
            }
          }
        }
      }

      // Aggregato: stato globale = peggior issue tra le sessioni
      const overall: WahaHealthStatus =
        issues.length === 0 ? 'healthy' :
        issues.some((i) => i.kind === 'session_broken') ? 'error' :
        'warning';
      const overallMessage =
        issues.length === 0 ? null :
        issues.length === 1 ? `${issues[0].label}: ${issues[0].detail}` :
        `${issues.length} numeri con problemi (passa il mouse per dettagli)`;

      set({
        wahaHealthStatus: overall,
        wahaHealthMessage: overallMessage,
        wahaLastCheck: new Date().toISOString(),
        wahaSessionsIssues: issues,
      });
    } catch (error) {
      console.error('[API Store] Error fetching WAHA health:', error);
    }
  },

  // Subscribe to realtime WAHA health updates
  subscribeToWahaHealth: () => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let visibleUserId: string | null = null;

    // Get user ID and setup subscription
    const setupSubscription = async () => {
      try {
        const userData = await getUserData();
        visibleUserId = userData.userId;
      } catch (e) {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return;
        visibleUserId = userData.user.id;
      }

      if (!visibleUserId) return;

      channel = supabase
        .channel('waha-health-changes')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'api_configurations',
            filter: `service_name=eq.waha`
          },
          (payload) => {
            // Only update if it's our user's config
            const newData = payload.new as any;
            if (newData && (newData.user_id === visibleUserId || newData.user_id === null)) {
              set({
                wahaHealthStatus: newData.session_health_status || null,
                wahaHealthMessage: newData.session_health_message || null,
                wahaLastCheck: newData.session_last_check || null
              });
            }
          }
        )
        .subscribe();
    };

    setupSubscription();

    // Return cleanup function
    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  },
}));